import { motion } from "framer-motion";
import { Zap, TrendingDown, Clock, Search } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionGlow from "./SectionGlow";
import { ease, duration, stagger } from "@/lib/motion";
const stats = [
  { icon: TrendingDown, value: "7%", label: "conversion loss per extra second of load time", color: "text-red-400" },
  { icon: Clock, value: "53%", label: "of mobile visitors bounce after 3 seconds", color: "text-amber-400" },
  { icon: Search, value: "#1", label: "Google ranks faster sites higher in local results", color: "text-sage" },
];

const WhySpeedSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      id="why-speed"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-section-mid overflow-hidden relative lg:pt-6"
      style={{ zIndex: 8 }}
      data-dark-section
    >
      <SectionGlow placement="bottom-center" />
      <div className="container mx-auto py-16 md:py-20 lg:py-0">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 0 : 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-sage/20 bg-sage/5 px-4 py-1.5 mb-5">
              <Zap className="h-3.5 w-3.5 text-sage" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-sage/90">Performance = Revenue</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mb-4 font-bold leading-[1.1]">
              Every Extra Second Costs You <span className="text-accent-sweep">7%</span> of Conversions.
            </h2>
            <p className="text-cream/60 leading-relaxed md:text-lg max-w-md">
              Slow sites kill conversions. Every site we build is engineered for speed, mobile, and search from day one.
            </p>
          </motion.div>

          {isMobile ? (
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: duration.smooth, ease: ease.smooth }}
            >
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-5 bg-charcoal rounded-xl border border-cream/5 p-5 group"
                >
                  <div className="h-12 w-12 shrink-0 rounded-lg bg-cream/5 flex items-center justify-center">
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className={`font-heading text-2xl font-bold mb-0.5 ${stat.color}`}>{stat.value}</p>
                    <p className="text-sm text-cream/60 leading-relaxed">{stat.label}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            <div className="space-y-4">
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: duration.smooth, delay: i * stagger, ease: ease.smooth }}
                  className="flex items-center gap-5 bg-charcoal rounded-xl border border-cream/5 p-5 md:p-6 group hover:border-sage/20 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
                >
                  <div className="h-12 w-12 shrink-0 rounded-lg bg-cream/5 flex items-center justify-center group-hover:bg-sage/10 transition-colors duration-300">
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className={`font-heading text-2xl md:text-3xl font-bold mb-0.5 ${stat.color}`}>{stat.value}</p>
                    <p className="text-sm text-cream/60 leading-relaxed">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhySpeedSection;
